import * as React from 'react';
import { WPSClient, getWidgetProps, RequestContext, DestructuredURL, NavLink, Args, NavLanguage, safeBool, StringMap } from 'DVWebNodeUtils';
import { Logo, MainMenu, SearchBar, LocaleSelector, LocaleSelectorModalContent, YourAccount, Placeholder, SignUpCTA } from './components'
import { LocaleSelectionSubMenuProps } from './components/YourAccount'
import transformNav from './transforms';
import { NavProfiles } from '../Shared/transforms/Profiles';

require('./index.module.scss');
// Client-side only Javascript
if (!global.__SERVER__) {
  require('./scripts/nav');
}

interface LogoProps {
  targetUrl: string
  altText: string
  imageURL?: string
}

interface MainMenuProps {
  text: string
  links: NavLink[]
}

interface SearchHistoryStrings {
  recent: string,
  suggested: string,
  clear: string,
  clearInfoTitle: string,
  clearInfoButton: string,
  clearInfoMessage: string,
  cancel: string,
  clearInfoAck: string
}

interface SearchHistory {
  treatment: string,
  strings: SearchHistoryStrings
}

interface SearchBarProps {
  hasSignUpCTA: Boolean;
  searchFormAction: DestructuredURL,
  searchFormPlaceholder: string,
  searchPhrase: string,
  searchSuggestionsEndpoint: DestructuredURL,
  altText: string,
  searchHistory: SearchHistory
}

interface YourAccountProps {
  altText: string,
  links: NavLink[],
  localeSelectionSubMenu: LocaleSelectionSubMenuProps,
  params: NavParamsProps
}

interface LocaleSelectorProps {
  text: string,
  locales: NavLink[],
  languages: NavLanguage[],
  languageToken: string
}

interface SignUpCTAProps {
  text: string,
  href: string
}

export interface NavParamsProps {
  firstName: string,
  languageToken: string
  signInLink: string
  isLanguageModal?: string
  isLanguageModalIndia?: string
  searchHistoryTreatment: string
}

export interface NavProps {
    logo: LogoProps,
    mainMenu?: MainMenuProps,
    searchBar?: SearchBarProps,
    yourAccount?: YourAccountProps,
    cerberus?: NavProfiles,
    localeSelector?: LocaleSelectorProps,
    signUpCTA?: SignUpCTAProps,
    context: RequestContext,
}

const getProps: getWidgetProps<NavProps> =
    global.__SERVER__ === true
        ? async ({ context, searchPhrase, firstName, pageGating, isCrow, isWaymark, isElcano, languageToken, nodeCache, signInLink, isLanguageModal, isLanguageModalIndia, searchHistoryTreatment, isProfilesEnabled }, api) => {
              const isCerberusEnabled = safeBool(isProfilesEnabled)
              const wpsClient = new WPSClient(api)
              const params = { firstName, languageToken, signInLink, isLanguageModal, isLanguageModalIndia, searchHistoryTreatment }
              try {
                const [navResponse, getProfilesResponse] = await Promise.all([
                wpsClient.Navigation({ context, pageGating, searchPhrase, isCrow: safeBool(isCrow), isWaymark: safeBool(isWaymark), isElcano: safeBool(isElcano) }, nodeCache),
                (isCerberusEnabled ? wpsClient.GetProfiles({ withAdditionalData: true, context }) : null)
                ])
              return transformNav(navResponse, context, params, {
                isCerberusEnabled: isCerberusEnabled, 
                data: getProfilesResponse})
              } catch (exception) {
                /* TODO: Add Navigation response fallback */
                api.logger.count('DVWebNodeNav-NavProfilesFallback', 1, true)
                const response = await wpsClient.Navigation({ context, pageGating, searchPhrase, isCrow: safeBool(isCrow), isWaymark: safeBool(isWaymark), isElcano: safeBool(isElcano) }, nodeCache)
                transformNav(response, context, params, {
                  isCerberusEnabled: false, 
                  data: null})
              }
          }
        : undefined;

const isLocaleSelectorModal = (context: RequestContext, localeSelector: LocaleSelectorProps, yourAccount: YourAccountProps) => {
  return Boolean(context.customerID && localeSelector && localeSelector.locales.length &&
    yourAccount && yourAccount.links.length && yourAccount.links.find(l => l.isPanel === true));
}

const NavWidget = ({
    logo,
    mainMenu,
    searchBar,
    yourAccount,
    cerberus,
    localeSelector,
    context,
    signUpCTA,
    logger
  }: NavProps & Args) => {
    const showLocaleSelectorModal = isLocaleSelectorModal(context, localeSelector, yourAccount);
    const localeSelectorModal = showLocaleSelectorModal ? <LocaleSelectorModalContent {...localeSelector} /> : null
    const cerberusContainerClass = cerberus && cerberus.activeProfile.ageGroup === "CHILD" ? "pv-nav-cerberus-kids" : ''
    
    return (
        <div className={`pv-nav-container ${cerberusContainerClass}`}>
          <Logo {...logo} />
          <div className="pv-nav-primary-components">
            {mainMenu ? <MainMenu {...mainMenu} /> : <Placeholder />}
            {searchBar && <SearchBar {...searchBar} logger={logger} context={context} />}
            {signUpCTA && <SignUpCTA {...signUpCTA}/>}
            {(!showLocaleSelectorModal && localeSelector) && <LocaleSelector {...localeSelector} />}
            <YourAccount {...yourAccount} vtr={context.currentTerritory} localeSelectorModal={localeSelectorModal} cerberus={cerberus} />
          </div>
        </div>
    )
  }

export const Nav = {
    name: 'nav',
    getProps,
    Component: NavWidget,
    container: {
      tagName: 'header',
      ID: 'pv-nav',
      classes: ['pv-nav', 'av-blur-backdrop']
    }
};
